/**
 * Learner data that several dashboard widgets and the page shell (sidebar, top bar, tasks widget) show.
 *
 * Each piece has ONE React Query key here, so a page makes one request for it however many widgets
 * display it, and anything that refreshes the key refreshes every widget. Before, the same endpoint was
 * fetched under different keys (or in plain effects) — up to four times per dashboard load
 * (dashboard audit 2026-11-02).
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserLearningApiService } from '@/services/user.service';
import { MyTasksApi } from '@/services/myTasks.service';
import { MarkingApi } from '@/services/assessment.service';
import { OjtEvaluationsApi, onOjtQueueChange } from '@/services/ojtActivity.service';
import { ProficiencyApi } from '@/services/proficiency.service';
import { LearningPathApi } from '@/services/learning-path.service';

export const SHARED_KEYS = {
  myLearning: ['my-learning'] as const,
  myTasks: (userId: unknown) => ['me-tasks', userId] as const,
  markingQueue: ['work-queue', 'marking'] as const,
  ojtQueue: ['work-queue', 'ojt'] as const,
  ojtEvaluator: ['work-queue', 'ojt-evaluator'] as const,
  mySkillStatus: ['my-skill-status'] as const,
  myProgramProgress: ['my-program-progress'] as const,
};

const asLen = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

/** The learner's courses and programs (My learning feed, first 200). */
export const useMyLearning = (enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.myLearning,
    queryFn: () => UserLearningApiService.getMyLearning(0, 200),
    enabled,
    staleTime: 2 * 60 * 1000,
  });

/** Tasks due this month (/me/tasks). */
export const useMyTasks = (userId: unknown, enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.myTasks(userId),
    queryFn: () => MyTasksApi.get(),
    enabled: enabled && !!userId,
    staleTime: 60 * 1000,
  });

/** Essay answers waiting for me to mark (0 when I'm not a marker). */
export const useMarkingQueueCount = (enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.markingQueue,
    queryFn: () => MarkingApi.getQueue().then(asLen).catch(() => 0),
    enabled,
    retry: false,
  });

/** OJT records waiting for my evaluation (0 when I'm not an evaluator). Re-counted when a record changes
 *  hands or the window regains focus. */
export const useOjtQueueCount = (enabled = true) => {
  const qc = useQueryClient();
  useEffect(
    () => (enabled ? onOjtQueueChange(() => { void qc.invalidateQueries({ queryKey: SHARED_KEYS.ojtQueue }); }) : undefined),
    [enabled, qc],
  );
  return useQuery({
    queryKey: SHARED_KEYS.ojtQueue,
    queryFn: () => OjtEvaluationsApi.queue().then(asLen).catch(() => 0),
    enabled,
    retry: false,
  });
};

/** Whether I evaluate any OJT (course SME or a named supervisor). */
export const useOjtEvaluator = (enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.ojtEvaluator,
    queryFn: () => OjtEvaluationsApi.amEvaluator().catch(() => false),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

/** My skills with required / current ratings (/skills/status/my). */
export const useMySkillStatus = (enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.mySkillStatus,
    queryFn: () => ProficiencyApi.getMyStatus('skill'),
    enabled,
    staleTime: 60 * 1000,
    retry: false,
  });

/** My programs and certifications with progress (/learning-paths/progress/my). */
export const useMyProgramProgress = (enabled = true) =>
  useQuery({
    queryKey: SHARED_KEYS.myProgramProgress,
    queryFn: () => LearningPathApi.getMyProgress(),
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
